import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import { 
  Box, 
  Container, 
  Typography, 
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { LocationOn, MyLocation } from '@mui/icons-material';

const LocationCheck = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [location, setLocation] = useState(null);

  useEffect(() => {
    checkLocationAndNavigate();
  }, []);

  const checkLocationAndNavigate = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🔍 LocationCheck: Starting location check...');

      // Check if user has location stored
      const storedLocation = localStorage.getItem('userLocation');
      
      console.log('🔍 LocationCheck: Stored location:', storedLocation);
      
      if (storedLocation) {
        // User has location, go directly to home
        console.log('🔄 LocationCheck: Location found, navigating to home');
        navigate('/home');
        return;
      }

      // Try to get current location
      if (navigator.geolocation) {
        console.log('🔍 LocationCheck: Getting current location...');
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            
            console.log('✅ LocationCheck: Location obtained:', { latitude, longitude });
            
            // Get address from coordinates using backend API
            try {
              // Get auth token
              const customerToken = localStorage.getItem('customerToken');
              const token = localStorage.getItem('token');
              const authToken = customerToken || token;
              
              const headers = {
                'Content-Type': 'application/json',
              };
              if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
              }
              
              const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.nistylez.com/api'}/locations/reverse-geocode`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                  lat: latitude,
                  lng: longitude
                })
              });
              
              const data = await response.json();
              
              const locationData = {
                latitude,
                longitude,
                address: data.success && data.data ? data.data.formatted_address : 'Current Location',
                type: 'current'
              };
              
              console.log('✅ LocationCheck: Location data with address:', locationData);
              
              // Store location
              localStorage.setItem('userLocation', JSON.stringify(locationData));
              setLocation(locationData);
              
              // Navigate to home
              console.log('🔄 LocationCheck: Navigating to home after getting location');
              navigate('/home');
            } catch (error) {
              console.error('❌ LocationCheck: Reverse geocoding failed:', error);
              // Store basic location data
              const locationData = {
                latitude,
                longitude,
                address: 'Current Location',
                type: 'current'
              };
              localStorage.setItem('userLocation', JSON.stringify(locationData));
              setLocation(locationData);
              navigate('/home');
            }
          },
          (error) => {
            console.error('❌ LocationCheck: Geolocation error:', error);
            setError('Unable to get your location automatically. Please set your location manually.');
            setLoading(false);
          },
          {
            timeout: 10000,
            enableHighAccuracy: false,
            maximumAge: 300000 // 5 minutes
          }
        );
      } else {
        console.log('❌ LocationCheck: Geolocation not supported');
        setError('Geolocation is not supported by your browser. Please set your location manually.');
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ LocationCheck: Location check error:', error);
      setError('Failed to check location. Please try again.');
      setLoading(false);
    }
  };

  const handleManualLocation = () => {
    console.log('🔄 LocationCheck: User chose manual location');
    navigate('/location');
  };

  const handleSkipLocation = () => {
    // Navigate to home without location
    console.log('🔄 LocationCheck: User chose to skip location, navigating to home');
    navigate('/home');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
            Checking your location...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            We're getting your location to show you nearby services
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center' }}>
        <LocationOn sx={{ fontSize: 80, color: '#006AF9', mb: 3 }} />
        
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Location Required
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          To provide you with the best experience, we need to know your location to show nearby services and providers.
        </Typography>

        {error && (
          <Alert severity="warning" sx={{ mb: 4, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<MyLocation />}
            onClick={handleManualLocation}
            sx={{ 
              py: 1.5,
              backgroundColor: '#006AF9',
              '&:hover': {
                backgroundColor: '#0056cc'
              }
            }}
          >
            Set Location Manually
          </Button>
          
          <Button
            variant="outlined"
            size="large"
            onClick={handleSkipLocation}
            sx={{ py: 1.5 }}
          >
            Skip for Now
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
          You can always update your location later in settings
        </Typography>
      </Box>
    </Container>
  );
};

export default LocationCheck; 